<template>
  <div class="dossier-card" :class="{ 'dossier-card--view': isViewMode }">
    <!-- ─── Cabeçalho do card ─── -->
    <div class="dossier-card__header" @click="toggleCollapse">
      <div class="dossier-card__header-left">
        <!-- Ícone de status agregado -->
        <span class="dossier-card__status-icon" :class="headerStatusClass">
          <i :class="headerStatusIcon"></i>
        </span>

        <div class="dossier-card__header-info">
          <h3 class="dossier-card__title">{{ title }}</h3>
          <!-- Resumo de status exibido no cabeçalho quando colapsado -->
          <span
            v-if="isCollapsed && statusSummary"
            class="dossier-card__status-summary"
          >
            {{ statusSummary }}
          </span>
        </div>
      </div>

      <div class="dossier-card__header-right">
        <!-- Badge de pendência/alerta de vencimento -->
        <span
          v-if="isExpiringSoon && !isViewMode"
          class="dossier-card__alert-badge"
        >
          <i class="fas fa-exclamation-triangle"></i>
          Vence em {{ daysUntilExpiry }}d
        </span>
        <span
          v-if="isExpired && !isViewMode"
          class="dossier-card__expired-badge"
        >
          <i class="fas fa-times-circle"></i>
          Vencido
        </span>

        <!-- Botão colapsar/expandir -->
        <button
          class="dossier-card__toggle"
          type="button"
          :aria-expanded="!isCollapsed"
        >
          <i
            class="fas"
            :class="isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
          ></i>
        </button>
      </div>
    </div>

    <!-- ─── Corpo colapsável ─── -->
    <div v-show="!isCollapsed" class="dossier-card__body">
      <!-- Alerta: documento não aplicável (módulos condicionais) -->
      <div v-if="notApplicable" class="dossier-card__not-applicable">
        <i class="fas fa-minus-circle mr-2"></i>
        Não aplicável para esta instituição
      </div>

      <!-- Conteúdo principal quando aplicável -->
      <template v-else>
        <!-- ── Texto explicativo (só no modo edição) ── -->
        <div
          v-if="!isViewMode && explainerText"
          class="dossier-card__explainer"
        >
          <button
            class="dossier-card__explainer-toggle"
            type="button"
            @click="showExplainer = !showExplainer"
          >
            <i class="fas fa-info-circle mr-1"></i>
            O que é este documento?
            <i
              class="fas ml-1"
              :class="showExplainer ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </button>
          <div v-show="showExplainer" class="dossier-card__explainer-text">
            <!-- Aceita HTML para links (ex: Receita Federal) -->
            <p v-html="explainerText"></p>
          </div>
        </div>

        <!-- ── Slot para campos específicos do módulo (acima do status) ── -->
        <slot name="fields-before"></slot>

        <!-- ── Status ── -->
        <div v-if="!isViewMode" class="dossier-card__field-group">
          <label class="dossier-card__label dossier-card__label--required">
            Situação do documento
          </label>

          <!-- Múltipla escolha -->
          <div v-if="multipleStatus" class="dossier-card__checkboxes">
            <label
              v-for="option in statusOptions"
              :key="option.value"
              class="dossier-card__checkbox-item"
            >
              <input
                type="checkbox"
                :value="option.value"
                v-model="internalValue.status"
                class="dossier-card__checkbox"
              />
              <span class="dossier-card__checkbox-label">{{
                option.label
              }}</span>
            </label>
          </div>

          <!-- Seleção única -->
          <div v-else class="dossier-card__radios">
            <label
              v-for="option in statusOptions"
              :key="option.value"
              class="dossier-card__radio-item"
            >
              <input
                type="radio"
                :value="option.value"
                v-model="internalValue.status"
                class="dossier-card__radio"
              />
              <span class="dossier-card__radio-label">{{ option.label }}</span>
            </label>
          </div>
        </div>

        <!-- Status em modo visualização -->
        <div v-else-if="hasStatus" class="dossier-card__view-status">
          <span class="dossier-card__label">Situação:</span>
          <span class="dossier-card__view-value">{{ displayStatus }}</span>
        </div>

        <!-- ── Validade ── -->
        <div
          v-if="showValidityField"
          class="dossier-card__field-group dossier-card__field-group--inline"
        >
          <div class="dossier-card__field">
            <label
              class="dossier-card__label"
              :class="{ 'dossier-card__label--required': isValidityRequired }"
            >
              Validade do documento
            </label>

            <!-- Modo edição -->
            <v-text-field
              v-if="!isViewMode"
              v-model="internalValue.validity_date"
              type="date"
              dense
              outlined
              hide-details="auto"
              :rules="isValidityRequired ? [rules.required] : []"
              class="dossier-card__date-input"
            />

            <!-- Modo visualização -->
            <span v-else class="dossier-card__view-value">
              <i class="fas fa-calendar-alt mr-1"></i>
              {{ formatDateBR(internalValue.validity_date) }}
            </span>
          </div>
        </div>

        <!-- ── Slot para campos específicos do módulo (abaixo do status) ── -->
        <slot name="fields-after"></slot>

        <!-- ── Upload / visualização do arquivo ── -->
        <div class="dossier-card__field-group">
          <label
            class="dossier-card__label"
            :class="{ 'dossier-card__label--required': isUploadRequired }"
          >
            Arquivo do documento (PDF)
          </label>

          <!-- Documento já anexado → exibe inline ou link -->
          <div
            v-if="internalValue.file_url"
            class="dossier-card__file-attached"
          >
            <div class="dossier-card__file-info">
              <i class="fas fa-file-pdf dossier-card__file-icon"></i>
              <span class="dossier-card__file-name">
                {{ internalValue.file_name || "Documento anexado" }}
              </span>
            </div>

            <div class="dossier-card__file-actions">
              <!-- Visualizar PDF inline (toggle) -->
              <v-btn
                small
                text
                class="btn-certified"
                @click="showPdfInline = !showPdfInline"
              >
                <i
                  class="fas mr-1"
                  :class="showPdfInline ? 'fa-eye-slash' : 'fa-eye'"
                ></i>
                {{ showPdfInline ? "Ocultar" : "Visualizar" }}
              </v-btn>

              <!-- Download -->
              <v-btn
                small
                text
                class="btn-add-down ml-2"
                :href="internalValue.file_url"
                target="_blank"
              >
                <i class="fas fa-download mr-1"></i>
                Baixar
              </v-btn>

              <!-- Substituir (só no modo edição) -->
              <v-btn
                v-if="!isViewMode"
                small
                text
                class="btn-notcertified ml-2"
                @click="$refs.fileInput.click()"
              >
                <i class="fas fa-sync-alt mr-1"></i>
                Substituir
              </v-btn>
            </div>

            <!-- Renderização inline do PDF -->
            <div v-if="showPdfInline" class="dossier-card__pdf-viewer">
              <iframe
                :src="internalValue.file_url"
                class="dossier-card__pdf-iframe"
                frameborder="0"
                :title="`PDF: ${title}`"
              ></iframe>
            </div>
          </div>

          <!-- Documento não anexado -->
          <div v-else class="dossier-card__file-pending">
            <div class="dossier-card__pending-message">
              <i class="fas fa-exclamation-circle mr-2"></i>
              Documento não anexado
            </div>

            <!-- Botão de upload (só no modo edição) -->
            <v-btn
              v-if="!isViewMode"
              small
              class="btn-add mt-2"
              @click="$refs.fileInput.click()"
              :loading="uploadLoading"
            >
              <i class="fas fa-upload mr-1"></i>
              Anexar documento
            </v-btn>
          </div>

          <!-- Input file oculto (compartilhado entre os dois estados) -->
          <input
            v-if="!isViewMode"
            ref="fileInput"
            type="file"
            accept="application/pdf"
            class="dossier-card__file-input"
            @change="handleFileChange"
          />

          <!-- Feedback de upload em progresso -->
          <div v-if="uploadLoading" class="dossier-card__upload-progress">
            <i class="fas fa-spinner fa-spin mr-1"></i>
            Enviando arquivo...
          </div>

          <!-- Erro de upload -->
          <div v-if="uploadError" class="dossier-card__upload-error">
            <i class="fas fa-times-circle mr-1"></i>
            {{ uploadError }}
          </div>
        </div>

        <!-- ── Observações ── -->
        <div class="dossier-card__field-group">
          <label class="dossier-card__label">Observações</label>
          <v-textarea
            v-if="!isViewMode"
            v-model="internalValue.observations"
            dense
            outlined
            rows="2"
            auto-grow
            hide-details
            placeholder="Informações complementares (opcional)"
            class="dossier-card__textarea"
          />
          <p
            v-else-if="internalValue.observations"
            class="dossier-card__view-value dossier-card__view-value--obs"
          >
            {{ internalValue.observations }}
          </p>
          <span v-else class="dossier-card__view-empty">—</span>
        </div>

        <!-- ── Slot para conteúdo extra no rodapé do card ── -->
        <slot name="footer"></slot>

        <!-- ── Botão salvar (modo edição) ── -->
        <div v-if="!isViewMode" class="dossier-card__actions">
          <v-btn
            class="btn-add"
            :loading="saveLoading"
            :disabled="!canSave"
            @click="save"
          >
            <i class="fas fa-save mr-1"></i>
            Salvar
          </v-btn>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import toast from "../../lib/ToastNotification";

export default {
  name: "DossierDocumentCard",

  props: {
    // Título exibido no cabeçalho do card
    title: {
      type: String,
      required: true,
    },

    // Opções de status. Formato: [{ value: 'vigente', label: 'Possui documento vigente' }]
    statusOptions: {
      type: Array,
      default: () => [],
    },

    // true → checkboxes (múltipla escolha) | false → radio (seleção única)
    multipleStatus: {
      type: Boolean,
      default: true,
    },

    // Valores de status que tornam a validade obrigatória
    statusRequiresValidity: {
      type: Array,
      default: () => ["vigente", "provisorio"],
    },

    // Valores de status que tornam o upload obrigatório
    statusRequiresUpload: {
      type: Array,
      default: () => ["vigente", "provisorio"],
    },

    // Texto explicativo (aceita HTML). Exibido só no modo edição.
    explainerText: {
      type: String,
      default: "",
    },

    // Modo de exibição: 'edit' | 'view'
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },

    is_applicable_answer: {
      type: String,
      default: "nao",
    },

    type: {
      type: String,
    },

    activeServices: {
      type: Array,
      default: () => [],
    },

    // Dados do módulo (v-model)
    value: {
      type: Object,
      default: () => ({
        status: [],
        validity_date: null,
        file_url: null,
        file_name: null,
        observations: "",
      }),
    },

    // Endpoint da API para salvar este módulo
    // Ex: "/rest-home/dossier/cnpj"
    saveEndpoint: {
      type: String,
      default: "",
    },

    // Endpoint da API para upload do arquivo
    // Ex: "/rest-home/dossier/cnpj/upload"
    uploadEndpoint: {
      type: String,
      default: "",
    },

    // Se true, exibe como "não aplicável" (módulos condicionais)
    notApplicable: {
      type: Boolean,
      default: false,
    },

    // Inicializa colapsado
    startCollapsed: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Cópia interna para não mutar a prop diretamente
      internalValue: this.deepClone(this.value),

      isCollapsed: this.startCollapsed,
      showExplainer: false,
      showPdfInline: false,

      saveLoading: false,
      uploadLoading: false,
      uploadError: "",

      rules: {
        required: (v) => !!v || "Campo obrigatório",
      },
    };
  },

  watch: {
    // Sincroniza se o pai atualizar o value externamente
    value: {
      deep: true,
      handler(newVal) {
        // Só atualiza se realmente diferente — evita loop
        if (JSON.stringify(newVal) !== JSON.stringify(this.internalValue)) {
          this.internalValue = this.deepClone(newVal);
        }
      },
    },

    // Emite para o pai sempre que algo mudar
    internalValue: {
      deep: true,
      handler(newVal) {
        // Só emite se realmente diferente do value recebido — evita loop
        if (JSON.stringify(newVal) !== JSON.stringify(this.value)) {
          this.$emit("input", this.deepClone(newVal));
        }
      },
    },
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },

    // ── Status ──────────────────────────────────────────────────────────────

    hasStatus() {
      if (Array.isArray(this.internalValue.status)) {
        return this.internalValue.status.length > 0;
      }
      return !!this.internalValue.status;
    },

    // Array normalizado de status selecionados (funciona para radio e checkbox)
    selectedStatusArray() {
      if (!this.internalValue.status) return [];
      if (Array.isArray(this.internalValue.status))
        return this.internalValue.status;
      return [this.internalValue.status];
    },

    // Texto do status para modo visualização
    displayStatus() {
      const selected = this.selectedStatusArray;
      if (!selected.length) return "—";
      return selected
        .map((v) => {
          const opt = this.statusOptions.find((o) => o.value === v);
          return opt ? opt.label : v;
        })
        .join(", ");
    },

    // Texto resumido para o cabeçalho colapsado
    statusSummary() {
      return this.displayStatus !== "—" ? this.displayStatus : null;
    },

    // ── Regras de obrigatoriedade ────────────────────────────────────────────

    isValidityRequired() {
      return this.selectedStatusArray.some((s) =>
        this.statusRequiresValidity.includes(s)
      );
    },

    isUploadRequired() {
      return this.selectedStatusArray.some((s) =>
        this.statusRequiresUpload.includes(s)
      );
    },

    // Exibe o campo de validade se a regra exigir OU se já houver uma data salva
    showValidityField() {
      return this.isValidityRequired || !!this.internalValue.validity_date;
    },

    // ── Alertas de vencimento ────────────────────────────────────────────────

    daysUntilExpiry() {
      if (!this.internalValue.validity_date) return null;
      const today = new Date();
      const expiry = new Date(this.internalValue.validity_date);
      return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    },

    isExpiringSoon() {
      const d = this.daysUntilExpiry;
      return d !== null && d > 0 && d <= 30;
    },

    isExpired() {
      const d = this.daysUntilExpiry;
      return d !== null && d <= 0;
    },

    // ── Ícone e cor do cabeçalho ─────────────────────────────────────────────

    // Retorna o estado geral do card: 'ok' | 'pending' | 'expiring' | 'expired' | 'not-applicable'
    overallState() {
      if (this.notApplicable) return "not-applicable";
      if (!this.hasStatus && !this.internalValue.file_url) return "pending";
      if (this.isExpired) return "expired";
      if (this.isExpiringSoon) return "expiring";
      if (this.isUploadRequired && !this.internalValue.file_url)
        return "pending";
      return "ok";
    },

    headerStatusClass() {
      const map = {
        ok: "dossier-card__status-icon--ok",
        pending: "dossier-card__status-icon--pending",
        expiring: "dossier-card__status-icon--expiring",
        expired: "dossier-card__status-icon--expired",
        "not-applicable": "dossier-card__status-icon--na",
      };
      return map[this.overallState] || "";
    },

    headerStatusIcon() {
      const map = {
        ok: "fas fa-check-circle",
        pending: "fas fa-circle",
        expiring: "fas fa-exclamation-triangle",
        expired: "fas fa-times-circle",
        "not-applicable": "fas fa-minus-circle",
      };
      return map[this.overallState] || "fas fa-circle";
    },

    // ── Pode salvar ──────────────────────────────────────────────────────────

    canSave() {
      // Bloqueia salvar se upload for obrigatório mas não houver arquivo
      if (this.isUploadRequired && !this.internalValue.file_url) return false;
      // Bloqueia se validade for obrigatória mas não estiver preenchida
      if (this.isValidityRequired && !this.internalValue.validity_date)
        return false;
      return true;
    },
  },

  methods: {
    // ── Colapsar ─────────────────────────────────────────────────────────────

    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    // ── Salvar ───────────────────────────────────────────────────────────────

    async save() {
      if (!this.saveEndpoint) {
        // Sem endpoint: apenas emite evento para o pai tratar
        this.$emit("save", this.deepClone(this.internalValue));
        return;
      }

      try {
        this.saveLoading = true;

        const response = await api.post(this.saveEndpoint, {
          ...this.internalValue,
          is_applicable_answer: this.is_applicable_answer,
          type: this.type,
          active_services: this.activeServices,
        });
        const { error, errors, message } = response.data;

        if (error) {
          this.$emit("error", errors);
        } else {
          toast(message || "Salvo com sucesso", "success");
          this.$emit("saved", this.deepClone(this.internalValue));
        }
      } catch (err) {
        console.error("DossierDocumentCard.save:", err);
      } finally {
        this.saveLoading = false;
      }
    },

    // ── Upload de arquivo ────────────────────────────────────────────────────

    async handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validação de tipo
      if (file.type !== "application/pdf") {
        this.uploadError = "Apenas arquivos PDF são aceitos.";
        return;
      }

      // Validação de tamanho (20MB)
      if (file.size > 20 * 1024 * 1024) {
        this.uploadError = "O arquivo deve ter no máximo 20MB.";
        return;
      }

      this.uploadError = "";

      if (!this.uploadEndpoint) {
        // Sem endpoint: emite o arquivo para o pai tratar
        this.$emit("file-selected", file);
        // Preview local temporário com URL de objeto
        this.internalValue.file_url = URL.createObjectURL(file);
        this.internalValue.file_name = file.name;
        return;
      }

      try {
        this.uploadLoading = true;

        const formData = new FormData();
        formData.append("file", file, file.name);

        const response = await api.post(this.uploadEndpoint, formData);
        const { error, errors, message, file_url, file_name } = response.data;

        if (error) {
          this.uploadError =
            errors?.[0]?.message || "Erro ao enviar o arquivo.";
        } else {
          this.internalValue.file_url = file_url;
          this.internalValue.file_name = file_name || file.name;
          this.showPdfInline = true;
          toast(message || "Arquivo anexado com sucesso", "success");
          this.$emit("file-uploaded", { file_url, file_name });
        }
      } catch (err) {
        this.uploadError = "Erro ao enviar o arquivo. Tente novamente.";
        console.error("DossierDocumentCard.upload:", err);
      } finally {
        this.uploadLoading = false;
        // Limpa o input para permitir re-upload do mesmo arquivo
        this.$refs.fileInput.value = "";
      }
    },

    // ── Utilitários ──────────────────────────────────────────────────────────

    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [year, month, day] = dateStr.split("-");
      return `${day}/${month}/${year}`;
    },

    deepClone(obj) {
      return JSON.parse(JSON.stringify(obj));
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER DOCUMENT CARD
   Componente base para todos os módulos de documento do Dossiê.
   Herda fontes e cores globais do projeto (WorkSans, #1f5a76, etc.)
   ═══════════════════════════════════════════════════════════════ */

/* ── Card container ─────────────────────────────────────────── */
.dossier-card {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.dossier-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Modo visualização: sem hover, visual mais limpo */
.dossier-card--view {
  border-color: #e8e8e8;
}

/* ── Cabeçalho ──────────────────────────────────────────────── */
.dossier-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f8f7;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  user-select: none;
}

.dossier-card__header:hover {
  background: #f0f0ef;
}

.dossier-card__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.dossier-card__header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.dossier-card__header-info {
  min-width: 0;
}

.dossier-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #33495e;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.dossier-card__status-summary {
  font-size: 12px;
  color: #666;
  display: block;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 400px;
}

/* ── Ícones de status no cabeçalho ──────────────────────────── */
.dossier-card__status-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.dossier-card__status-icon--ok {
  color: #30915b;
}
.dossier-card__status-icon--pending {
  color: #aaa;
}
.dossier-card__status-icon--expiring {
  color: #fb8b01;
}
.dossier-card__status-icon--expired {
  color: #dc3545;
}
.dossier-card__status-icon--na {
  color: #aaa;
}

/* ── Badges de alerta no cabeçalho ──────────────────────────── */
.dossier-card__alert-badge,
.dossier-card__expired-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.dossier-card__alert-badge {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
}

.dossier-card__expired-badge {
  background: #f8d7da;
  color: #842029;
  border: 1px solid #dc3545;
}

/* ── Botão toggle colapsar ───────────────────────────────────── */
.dossier-card__toggle {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  font-size: 13px;
  line-height: 1;
}

.dossier-card__toggle:hover {
  color: #1f5a76;
}

/* ── Corpo do card ───────────────────────────────────────────── */
.dossier-card__body {
  padding: 16px;
}

/* ── Não aplicável ───────────────────────────────────────────── */
.dossier-card__not-applicable {
  color: #888;
  font-size: 13px;
  padding: 8px 0;
  font-style: italic;
}

/* ── Texto explicativo ───────────────────────────────────────── */
.dossier-card__explainer {
  background: #eef6fb;
  border: 1px solid #b8d9ec;
  border-radius: 4px;
  margin-bottom: 16px;
  overflow: hidden;
}

.dossier-card__explainer-toggle {
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  font-size: 13px;
  color: #1f5a76;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.dossier-card__explainer-toggle:hover {
  background: #ddeef8;
}

.dossier-card__explainer-text {
  padding: 0 12px 12px;
  font-size: 13px;
  color: #444;
  line-height: 1.6;
  border-top: 1px solid #b8d9ec;
}

.dossier-card__explainer-text p {
  margin: 8px 0 0;
}

.dossier-card__explainer-text a {
  color: #1f5a76;
  word-break: break-all;
}

/* ── Grupos de campos ────────────────────────────────────────── */
.dossier-card__field-group {
  margin-bottom: 14px;
}

.dossier-card__field-group--inline {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.dossier-card__field {
  flex: 1;
  min-width: 200px;
}

/* ── Labels ──────────────────────────────────────────────────── */
.dossier-card__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}

.dossier-card__label--required::after {
  content: " *";
  color: #dc3545;
}

/* ── Checkboxes ──────────────────────────────────────────────── */
.dossier-card__checkboxes,
.dossier-card__radios {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dossier-card__checkbox-item,
.dossier-card__radio-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
}

.dossier-card__checkbox,
.dossier-card__radio {
  margin-top: 3px;
  flex-shrink: 0;
  accent-color: #1f5a76;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.dossier-card__checkbox-label,
.dossier-card__radio-label {
  font-size: 13px;
  color: #333;
  line-height: 1.5;
}

/* ── Input de data ───────────────────────────────────────────── */
.dossier-card__date-input {
  max-width: 220px;
}

/* ── Arquivo anexado ─────────────────────────────────────────── */
.dossier-card__file-attached {
  border: 1px solid #c3e6cb;
  background: #f4fff8;
  border-radius: 4px;
  padding: 10px 12px;
}

.dossier-card__file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.dossier-card__file-icon {
  font-size: 18px;
  color: #dc3545;
  flex-shrink: 0;
}

.dossier-card__file-name {
  font-size: 13px;
  color: #333;
  word-break: break-word;
}

.dossier-card__file-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* ── PDF viewer inline ───────────────────────────────────────── */
.dossier-card__pdf-viewer {
  margin-top: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.dossier-card__pdf-iframe {
  width: 100%;
  height: 520px;
  display: block;
}

/* ── Documento pendente ──────────────────────────────────────── */
.dossier-card__file-pending {
  border: 1px dashed #ccc;
  border-radius: 4px;
  padding: 10px 12px;
  background: #fafafa;
}

.dossier-card__pending-message {
  font-size: 13px;
  color: #888;
  font-style: italic;
}

/* ── Upload feedback ─────────────────────────────────────────── */
.dossier-card__file-input {
  display: none;
}

.dossier-card__upload-progress {
  margin-top: 8px;
  font-size: 13px;
  color: #1f5a76;
}

.dossier-card__upload-error {
  margin-top: 8px;
  font-size: 13px;
  color: #dc3545;
  font-weight: 600;
}

/* ── Textarea ────────────────────────────────────────────────── */
.dossier-card__textarea {
  font-size: 13px;
}

/* ── Modo visualização: valores ──────────────────────────────── */
.dossier-card__view-status {
  margin-bottom: 14px;
  font-size: 13px;
}

.dossier-card__view-value {
  font-size: 13px;
  color: #333;
}

.dossier-card__view-value--obs {
  font-style: italic;
  color: #555;
}

.dossier-card__view-empty {
  font-size: 13px;
  color: #aaa;
}

/* ── Ações (botão salvar) ────────────────────────────────────── */
.dossier-card__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0ef;
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 600px) {
  .dossier-card__header {
    padding: 10px 12px;
  }

  .dossier-card__body {
    padding: 12px;
  }

  .dossier-card__status-summary {
    max-width: 180px;
  }

  .dossier-card__file-actions {
    flex-direction: column;
  }

  .dossier-card__pdf-iframe {
    height: 340px;
  }

  .dossier-card__date-input {
    max-width: 100%;
  }
}
</style>